import { useFormState } from 'react-hook-form-mui';
import { pushTo } from '../../../core/routing';
import history from '../../../core/routing/history';
import { useSubmitShortcutHotkey } from '../hooks/useSubmitShortcutHotkey';
import { FormActionsMuiProps } from './form-actions-mui-types';

type UseFormActionsMuiParams = Pick<
  FormActionsMuiProps,
  | 'disableOnSubmit'
  | 'disableSave'
  | 'successState'
  | 'cancel'
  | 'onCancel'
  | 'onDiscard'
  | 'onDraft'
  | 'variant'
  | 'disableSubmitIfInvalid'
>;

/**
 * Derives form-action UI state (disabled flags, discard/draft mode, cancel handler, submit hotkey).
 */
export function useFormActionsMui({
  disableOnSubmit = false,
  disableSave = false,
  successState = false,
  cancel,
  onCancel,
  onDiscard,
  onDraft,
  variant = 'default',
  disableSubmitIfInvalid = false,
}: UseFormActionsMuiParams) {
  const state = useFormState();
  const { hotkey: submitHotkey, ref: submitShortcutRef } = useSubmitShortcutHotkey();

  const useDiscardDraft = onDiscard != null && onDraft != null;
  const allowCancel = useDiscardDraft ? false : (cancel ?? onCancel);

  const isSavedAndClean = successState && !state.isDirty;
  const showTick = isSavedAndClean && state.isSubmitted;
  const saveDisabled =
    disableSave || isSavedAndClean || (disableSubmitIfInvalid && state.isValid === false);
  const secondaryDisabled = state.isSubmitting || (disableOnSubmit && state.isSubmitted);

  const isCompact = variant === 'compact';
  const isSubmitLoading = state.isSubmitting || (disableOnSubmit && state.isSubmitted);
  // Keep the shortcut visible while disabled so users can discover it; hide only on tick/loading.
  const showSubmitHotkey = !showTick && !isSubmitLoading && !!submitHotkey;

  /** Navigates back or to a path when cancel is a boolean/string; otherwise calls onCancel. */
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }

    if (cancel === true) {
      history.goBack();
    } else if (typeof cancel === 'string') {
      pushTo(cancel);
    }
  };

  return {
    submitShortcutRef,
    submitHotkey,
    useDiscardDraft,
    allowCancel,
    showTick,
    saveDisabled,
    secondaryDisabled,
    isCompact,
    isSubmitLoading,
    showSubmitHotkey,
    handleCancel,
    onDiscard,
    onDraft,
  };
}
